// Tulevaisuustestaus, formerly Kuviteltu tulevaisuus (#946, #1246): future_reimagined.v1 — the `futureReimagined` namespace.
// Its own file so the module's strings grow here, not in fi.js / en.js / sv.js.

import { futureOutcomeFi, futureOutcomeEn, futureOutcomeSv } from './futureOutcomeStrings.js';

export const futureReimaginedFi = {
  title: "Tulevaisuustestaus",
  intro: "Ratkaisut on arvioitu yksi kerrallaan. Tässä vaiheessa katsotaan, mitä ne tekevät toisilleen, valitaan pidettävät ja kuvitellaan, miten valittu joukko voisi toteutua. Kyse on kuvitelmasta, ei ennustuksesta: epävarmuudet sanotaan ääneen.",
  loading: "Ladataan ratkaisuja…",
  loadError: "Tietojen lataus epäonnistui: {{message}}",
  tooFew: "Ristivaikutuksia voi tarkastella, kun Ratkaisut v2:ssa on vähintään kaksi ratkaisua. Nyt niitä on {{count}}.",
  unprojected: "Näille ratkaisuille ei ole vielä vaikutusarviota, joten ne puuttuvat mittarivertailusta: {{names}}",
  direction: { better: "Parantaa", worse: "Huonontaa", unclear: "Suunta, jonka hyvyys ei ole määritelty" },
  scenarioEditor: {
    title: "Ennusteen skenaariot",
    intro: "Skenaariot ovat maailmoja, joissa lukitut ratkaisut kuvitellaan ja arvioidaan realistisesti. Valitse, mistä ne tulevat.",
    source: "Lähde",
    sourceHint: {
      expert: "Asiantuntijoiden laatimat neljä skenaariota optimismin tason mukaan. Sama kaikissa työpajoissa.",
      workshop: "Työpajan omat skenaariot edeltävästä moduulista (esim. Ennakointi). Luetaan sellaisenaan, niitä ei muokata täällä.",
      own: "Tätä työpajaa varten kirjoitetut tekstit.",
    },
    inUse: "Käytössä",
    use: "Käytä tätä lähdettä",
    placeholder: "Asiantuntijaskenaariot ovat vielä paikkamerkkejä, kunnes asiantuntijoiden tekstit lisätään.",
    workshopEmpty: "Työpajalla ei ole vielä skenaarioita. Ne syntyvät edeltävässä moduulissa.",
    copyExpert: "Kopioi asiantuntijaskenaariot muokattaviksi",
    add: "Lisää skenaario",
    remove: "Poista skenaario",
    titleLabel: "Otsikko",
    levelLabel: "Optimismin taso",
    levelNone: "Ei tasoa",
    textLabel: "Teksti",
    chars: "{{n}} / {{max}} merkkiä",
    save: "Tallenna omat skenaariot",
    saved: "Tallennettu.",
    reset: "Poista omat tekstit",
    tooFew: "Tarvitaan vähintään {{min}} skenaariota, joissa on otsikko ja teksti.",
    loadError: "Skenaarioiden lataus epäonnistui: {{message}}",
  },
  matrix: {
    title: "Mitä kukin ratkaisu liikuttaa",
    hint: "Rivit ovat ratkaisuja, sarakkeet vipupisteiden mittareita. Väri tulee mittarin omasta suunnasta, ei tekoälyn arviosta ratkaisusta.",
    sideEffect: "Sivuvaikutus (ei tavoiteltu)",
    untouched: "yksikään ratkaisu ei liikuta",
  },
  candidates: {
    title: "Mahdolliset ristivaikutukset",
    hint: "Laskettu ratkaisujen vaikutusarvioista, tavoitteista ja toteutuspoluista: mitä kahdella ratkaisulla on yhteistä. Tämä ei vielä ole tulkinta siitä, tukevatko ne toisiaan vai kilpailevatko ne.",
    none: "Ratkaisuilla ei ole yhteisiä mittareita, tavoitteita eikä toimijoita.",
    all: "Näytä kaikki {{count}} paria",
    fewer: "Näytä vähemmän",
  },
  signal: {
    similarity: "Liikuttavat samoja mittareita {{percent}} %",
    opposed: "Vastakkain: {{name}}",
    same_harm: "Molemmat huonontavat: {{name}}",
    unclear_opposed: "Eri suuntiin: {{name}}",
    same_good: "Molemmat parantavat: {{name}}",
    unclear_same: "Samaan suuntaan: {{name}}",
    shared_target: "Sama tavoite: {{name}}",
    shared_actor: "Sama toimija: {{name}} ({{horizon}})",
  },
  interplay: {
    title: "Ristivaikutukset",
    hintUnread: "Alla näkyy, mitä ratkaisuilla on laskennallisesti yhteistä. Tekoäly lukee parit ja kertoo, menevätkö ratkaisut päällekkäin, kilpailevatko ne, ovatko ne ristiriidassa, mahdollistavatko tai vahvistavatko ne toisiaan – vai eivätkö ne vaikuta toisiinsa lainkaan.",
    hintRead: "Viivat ovat tekoälyn tulkinta siitä, miten ratkaisut vaikuttavat toisiinsa. Valitse ratkaisu nähdäksesi vain sen suhteet. Jokaisen suhteen perusta ja epävarmuus on kerrottu.",
    waiting: "Tekoäly ei ole vielä lukenut pareja. Fasilitaattori käynnistää luvun, ja tulos näkyy tässä, kun se valmistuu.",
    readAt: "Luettu {{time}}",
    stale: "Ratkaisuja on muutettu tai lisätty tämän lukeman jälkeen ({{names}}). Fasilitaattori voi lukea ristivaikutukset uudelleen ennen äänestystä.",
  },
  graph: { label: "Ratkaisujen väliset suhteet" },
  relation: { conflict: "Ristiriita", overlap: "Päällekkäisyys", competing: "Kilpailu", enables: "Mahdollistaa", reinforces: "Vahvistaa", independent: "Ei vaikutusta" },
  strength: { low: "Vähäinen merkitys", medium: "Kohtalainen merkitys", high: "Suuri merkitys" },
  basis: { research: "tutkimus", analogue: "vertailukohta", reasoned: "päättely" },
  cluster: { overlap: "Päällekkäinen ryhmä:", competing: "Kilpaileva ryhmä:", dependency: "Yhdestä riippuvat:" },
  relationCard: {
    why: "Miksi: perustelu, epävarmuus ja perusta",
    uncertainty: "Epävarmuus: {{text}}",
    via: "Kulkee: {{list}}",
    basis: "Perusta: {{basis}}",
    noneDrawn: "Tekoäly ei löytänyt pareilta päällekkäisyyksiä, kilpailua, ristiriitoja eikä riippuvuuksia.",
    showIndependent: "Näytä parit, jotka eivät vaikuta toisiinsa ({{count}})",
    hideIndependent: "Piilota parit, jotka eivät vaikuta toisiinsa ({{count}})",
    unanswered: "Tekoäly ei antanut arviota näille pareille: {{pairs}}",
    ragUnavailable: "Tutkimusaineistoon ei saatu yhteyttä, joten suhteet perustuvat päättelyyn.",
  },
  vote: {
    title: "Äänestys: mitkä ratkaisut pidetään",
    hint: "Valitse ratkaisut, jotka viedään tulevaisuustestaukseen. Kortissa näkyy, mihin ratkaisu on ristivaikutusten mukaan kytköksissä. Voit perua äänen napauttamalla uudelleen.",
    left: "Ääniä jäljellä {{left}} / {{budget}}",
    noIdentity: "Tämä laite ei voi äänestää. Kirjaudu työpajan PIN-koodilla.",
    vote: "Äänestä: {{name}}",
    unvote: "Peru ääni: {{name}}",
    warning: { conflict: "Ristiriidassa: {{name}}", overlap: "Päällekkäinen: {{name}}", competing: "Kilpailee: {{name}}", needs: "Tarvitsee ensin: {{name}}", enables: "Mahdollistaa: {{name}}" },
    error: {
      CLOSED: "Äänestys on suljettu.",
      BUDGET: "Äänesi ovat loppu. Peru jokin ääni, jos haluat äänestää toista.",
      NO_SOLUTION: "Ratkaisua ei enää ole.",
      NO_VOTER: "Tätä laitetta ei tunnistettu äänestäjäksi.",
      generic: "Äänen tallennus epäonnistui: {{message}}",
    },
  },
  locked: {
    title: "Tulevaisuustestaukseen viedään",
    votes: "(ääniä: {{count}})",
    override: "Valinta poikkeaa äänestyksestä. Fasilitaattorin perustelu: {{reason}}",
    stale: "Näitä ratkaisuja on muokattu lukituksen jälkeen: {{names}}. Kuviteltu aikajana perustuu lukittuun versioon.",
    deleted: "(poistettu ratkaisu)",
  },
  admin: {
    title: "Tulevaisuustestaus: äänestys ja lukitus",
    loadError: "Tietojen lataus epäonnistui: {{message}}",
    interplayRead: "Ristivaikutukset on luettu.",
    interplayStale: "Ristivaikutukset on luettu, mutta ratkaisuja on muutettu sen jälkeen. Lue ne uudelleen moduulin sivulla ennen äänestystä.",
    interplayMissing: "Ristivaikutuksia ei ole vielä luettu. Ne kannattaa lukea moduulin sivulla ennen äänestystä.",
    votes: "Äänestys {{open}}, {{budget}} ääntä laitetta kohden. Avaa ja sulje äänestys yllä olevasta kytkimestä.",
    open: "auki",
    closed: "suljettu",
    tension: {
      overlap: "Päällekkäisyys valinnassa: {{a}} ja {{b}}",
      conflict: "Ristiriita valinnassa: {{a}} ja {{b}}. Lukitseminen on silti mahdollista; aikajana näyttää, mitä se maksaa.",
    },
    overrideHint: "Valinta jättää pois ratkaisun, joka sai enemmän ääniä kuin jokin valituista. Kirjaa syy: huone näkee sen, ja se tallentuu raporttiin.",
    overridePlaceholder: "Miksi valinta poikkeaa äänestyksestä?",
    lock: "Lukitse {{count}} ratkaisua",
    relock: "Lukitse uudelleen ({{count}})",
    lockFailed: "Lukitus epäonnistui: {{message}}",
    openModule: "Avaa moduuli",
    currentLock: "Nykyinen lukitus ({{time}}): {{names}}",
  },
  course: {
    causes: "Perustelut",
    title: "Kuviteltu kulku: mitä ratkaisuille voisi käydä",
    running: "Aikajanaa kuvitellaan…",
    failed: "Aikajanan kuvittelu epäonnistui: {{message}}",
    yearLikelihood: "{{year}} · {{likelihood}}",
    triggers: "Mistä se johtuu:",
    setAside: "Uhat, joiden ei arvioida toteutuvan:",
    threat: "{{what}} ({{when}}, {{likelihood}})",
    relation: "{{relation}}: {{a}} – {{b}}",
    wouldChange: "Mikä muuttaisi tämän: {{text}}",
    missing: "Näille ratkaisuille ei saatu perusteltua kohtaloa: {{names}}",
    until: "{{year}} asti",
    after: "vasta kun {{name}} on käynnissä",
  },
  fate: { realised: "Toteutuu", delayed: "Viivästyy", diminished: "Heikkenee", failed: "Kaatuu" },
  likelihood: { low: "epätodennäköinen", medium: "mahdollinen", high: "todennäköinen" },
  timeline: {
    label: "Lukittujen ratkaisujen kuviteltu toteutus vuosittain",
    today: "Tänään",
    haze: "Ennusteen epävarmuus kasvaa pidemmälle tulevaisuuteen mentäessä",
    noNarrative: "Kertomusta ei saatu kirjoitettua; kulku ja kohtalot ovat silti alla. {{message}}",
  },
  narrativeKind: { milestone: "Virstanpylväs", side_effect: "Sivuvaikutus", setback: "Takaisku", turning_point: "Käännekohta", interaction: "Kohtaaminen" },
  wall: { vision: "Kuvitelma, ei ennuste: jokainen hetki perustuu ratkaisujen omiin uhkiin ja niiden välisiin suhteisiin, ja sen epävarmuus on kerrottu." },
  robustness: {
    title: "Kestävyys: miten ratkaisut pärjäävät eri kuluissa",
    verdict: "Yhteenveto",
    holds: "Kestää kaikissa kuluissa",
    failsAll: "Kaatuu kaikissa kuluissa",
    fails: "Kaatuu jossain kulussa",
    varies: "Vaihtelee kulun mukaan",
    unknown: "Ei tietoa",
  },
  outlook: {
    title: "Mitä mittareille voisi tapahtua",
    hint: "Ratkaisut v2:n vaikutusarviot vipupisteiden mittareihin, sijoitettuna tämän kulun vuosiin: viivästyvä ratkaisu alkaa myöhemmin, kaatuva päättyy kaatumiseensa. Heikkenevän ratkaisun käyrä on yläraja, ei arvio pienemmästä vaikutuksesta. Saman mittarin käyriä ei lasketa yhteen.",
    measured: "Suhteellinen muutos nykyhetkestä, mitattu suure",
    proposed: "Suuntakuva – ehdotettu mittari, ei mittausdataa",
    upper_bound: "yläraja",
    stops: "päättyy {{year}}",
  },
  report: {
    interplayIntro: "Ennen äänestystä luettiin, miten työpajan ratkaisut vaikuttavat toisiinsa. Suhteet perustuvat ratkaisujen vaikutusarvioihin, tavoitteisiin ja toteutuspolkuihin; jokaisella on perustelu ja epävarmuus.",
    independent: "{{count}} ratkaisuparilla ei arvioitu olevan merkittävää vaikutusta toisiinsa.",
    timelineIntro: "Työpaja äänesti pidettävistä ratkaisuista, ja valinta lukittiin. Sen jälkeen kuviteltiin, miten lukitut ratkaisut voisivat toteutua kymmenen vuoden aikana kussakin skenaariossa, arvioiden ne realistisesti siinä maailmassa. Kuvitelma, ei ennuste: jokainen kohtalo perustuu ratkaisujen omiin uhkiin ja niiden välisiin suhteisiin.",
    locked: "Tulevaisuustestaukseen vietiin: {{names}}",
    override: "Valinta poikkesi äänestyksestä. Perustelu: {{reason}}",
    fates: "Ratkaisujen kohtalot",
    moments: "Mitä yhteiskunnassa voitaisiin huomata",
  },
  ...futureOutcomeFi,
};

export const futureReimaginedEn = {
  title: "Future testing",
  intro: "Each solution has been evaluated on its own. This step looks at what they do to each other, chooses which to keep, and imagines how the chosen set could play out. It is a vision, not a forecast: uncertainties are stated openly.",
  loading: "Loading solutions…",
  loadError: "Loading failed: {{message}}",
  tooFew: "Interplay can be read once Solutions v2 has at least two solutions. There are {{count}} now.",
  unprojected: "These solutions have no impact estimate yet, so they are missing from the metric comparison: {{names}}",
  direction: { better: "Improves", worse: "Worsens", unclear: "Direction with no agreed better or worse" },
  scenarioEditor: {
    title: "Forecast scenarios",
    intro: "The scenarios are the worlds the locked solutions are imagined in and judged realistically. Choose where they come from.",
    source: "Source",
    sourceHint: {
      expert: "The four scenarios written by experts, by level of optimism. The same in every workshop.",
      workshop: "The workshop's own scenarios from a prerequisite module (e.g. Ennakointi). Read as they are, not edited here.",
      own: "Texts written for this workshop.",
    },
    inUse: "In use",
    use: "Use this source",
    placeholder: "The expert scenarios are still placeholders until the experts' texts are added.",
    workshopEmpty: "The workshop has no scenarios yet. They come from a prerequisite module.",
    copyExpert: "Copy the expert scenarios to edit",
    add: "Add a scenario",
    remove: "Remove the scenario",
    titleLabel: "Title",
    levelLabel: "Optimism level",
    levelNone: "No level",
    textLabel: "Text",
    chars: "{{n}} / {{max}} characters",
    save: "Save the own scenarios",
    saved: "Saved.",
    reset: "Remove the own texts",
    tooFew: "At least {{min}} scenarios with a title and a text are needed.",
    loadError: "Loading the scenarios failed: {{message}}",
  },
  matrix: {
    title: "What each solution moves",
    hint: "Rows are solutions, columns the leverage points' metrics. The colour comes from the metric's own direction, not from the AI's opinion of the solution.",
    sideEffect: "Side effect (not aimed at)",
    untouched: "no solution moves it",
  },
  candidates: {
    title: "Possible interplay",
    hint: "Computed from the solutions' impact estimates, targets and pathways: what two solutions have in common. It does not yet say whether they support or compete with each other.",
    none: "The solutions share no metrics, targets or actors.",
    all: "Show all {{count}} pairs",
    fewer: "Show fewer",
  },
  signal: {
    similarity: "Move the same metrics {{percent}}%",
    opposed: "Opposed: {{name}}",
    same_harm: "Both worsen: {{name}}",
    unclear_opposed: "Opposite directions: {{name}}",
    same_good: "Both improve: {{name}}",
    unclear_same: "Same direction: {{name}}",
    shared_target: "Same target: {{name}}",
    shared_actor: "Same actor: {{name}} ({{horizon}})",
  },
  interplay: {
    title: "Interplay",
    hintUnread: "Below is what the solutions have in common by the numbers. The AI reads the pairs and says whether the solutions overlap, compete, conflict, enable or reinforce each other — or do not affect each other at all.",
    hintRead: "The lines are the AI's reading of how the solutions affect each other. Select a solution to see only its relations. Each relation states its basis and uncertainty.",
    waiting: "The AI has not read the pairs yet. The facilitator starts the reading, and the result appears here when it is ready.",
    readAt: "Read {{time}}",
    stale: "Solutions have been changed or added since this reading ({{names}}). The facilitator can read the interplay again before the vote.",
  },
  graph: { label: "Relations between the solutions" },
  relation: { conflict: "Conflict", overlap: "Overlap", competing: "Competing", enables: "Enables", reinforces: "Reinforces", independent: "Independent" },
  strength: { low: "Minor", medium: "Moderate", high: "Major" },
  basis: { research: "research", analogue: "comparable case", reasoned: "reasoning" },
  cluster: { overlap: "Overlapping group:", competing: "Competing group:", dependency: "Resting on one:" },
  relationCard: {
    why: "Why: the reasoning, the uncertainty and the basis",
    uncertainty: "Uncertainty: {{text}}",
    via: "Runs through: {{list}}",
    basis: "Basis: {{basis}}",
    noneDrawn: "The AI found no overlap, competition, conflict or dependency between the pairs.",
    showIndependent: "Show pairs that do not affect each other ({{count}})",
    hideIndependent: "Hide pairs that do not affect each other ({{count}})",
    unanswered: "The AI gave no verdict for these pairs: {{pairs}}",
    ragUnavailable: "The research material could not be reached, so the relations rest on reasoning.",
  },
  vote: {
    title: "Vote: which solutions to keep",
    hint: "Choose the solutions to carry into future testing. Each card shows what the interplay reading connects it to. Tap again to take a vote back.",
    left: "Votes left {{left}} / {{budget}}",
    noIdentity: "This device cannot vote. Sign in with the workshop PIN.",
    vote: "Vote: {{name}}",
    unvote: "Take back vote: {{name}}",
    warning: { conflict: "Conflicts with: {{name}}", overlap: "Overlaps: {{name}}", competing: "Competes with: {{name}}", needs: "Needs first: {{name}}", enables: "Enables: {{name}}" },
    error: {
      CLOSED: "Voting is closed.",
      BUDGET: "You have no votes left. Take one back to vote for another.",
      NO_SOLUTION: "That solution no longer exists.",
      NO_VOTER: "This device was not recognised as a voter.",
      generic: "Saving the vote failed: {{message}}",
    },
  },
  locked: {
    title: "Carried into future testing",
    votes: "(votes: {{count}})",
    override: "The choice differs from the vote. The facilitator's reason: {{reason}}",
    stale: "These solutions were edited after the lock: {{names}}. The imagined timeline rests on the locked version.",
    deleted: "(deleted solution)",
  },
  admin: {
    title: "Future testing: vote and lock",
    loadError: "Loading failed: {{message}}",
    interplayRead: "The interplay has been read.",
    interplayStale: "The interplay has been read, but solutions have changed since. Read it again on the module page before the vote.",
    interplayMissing: "The interplay has not been read yet. It is worth reading on the module page before the vote.",
    votes: "Voting {{open}}, {{budget}} votes per device. Open and close voting with the switch above.",
    open: "open",
    closed: "closed",
    tension: {
      overlap: "Overlap in the selection: {{a}} and {{b}}",
      conflict: "Conflict in the selection: {{a}} and {{b}}. You can still lock; the timeline will show what it costs.",
    },
    overrideHint: "The selection leaves out a solution that got more votes than one chosen. Record why: the room sees it and the report keeps it.",
    overridePlaceholder: "Why does the selection differ from the vote?",
    lock: "Lock {{count}} solutions",
    relock: "Lock again ({{count}})",
    lockFailed: "Locking failed: {{message}}",
    openModule: "Open the module",
    currentLock: "Current lock ({{time}}): {{names}}",
  },
  course: {
    causes: "Reasons",
    title: "Imagined course: what could happen to the solutions",
    running: "The timeline is being imagined…",
    failed: "Imagining the timeline failed: {{message}}",
    yearLikelihood: "{{year}} · {{likelihood}}",
    triggers: "What causes it:",
    setAside: "Threats judged not to materialise:",
    threat: "{{what}} ({{when}}, {{likelihood}})",
    relation: "{{relation}}: {{a}} – {{b}}",
    wouldChange: "What would change this: {{text}}",
    missing: "No grounded fate came back for these solutions: {{names}}",
    until: "until {{year}}",
    after: "only once {{name}} is under way",
  },
  fate: { realised: "Realised", delayed: "Delayed", diminished: "Diminished", failed: "Fails" },
  likelihood: { low: "unlikely", medium: "possible", high: "likely" },
  timeline: {
    label: "The imagined implementation of the locked solutions, year by year",
    today: "Today",
    haze: "The forecast grows less certain the further into the future it reaches",
    noNarrative: "The narrative could not be written; the course and the fates are still below. {{message}}",
  },
  narrativeKind: { milestone: "Milestone", side_effect: "Side effect", setback: "Setback", turning_point: "Turning point", interaction: "Interaction" },
  wall: { vision: "A vision, not a forecast: every moment rests on the solutions' own threats and the relations between them, and its uncertainty is stated." },
  robustness: {
    title: "Robustness: how the solutions fare across the courses",
    verdict: "Summary",
    holds: "Holds in every course",
    failsAll: "Fails in every course",
    fails: "Fails in some course",
    varies: "Varies by course",
    unknown: "No information",
  },
  outlook: {
    title: "What could happen to the metrics",
    hint: "Solutions v2's estimates for the leverage points' metrics, placed in this course's years: a delayed solution starts later, a failing one ends where it fails. A diminished solution's curve is an upper bound, not an estimate of a smaller effect. Curves on one metric are not added together.",
    measured: "Relative change from today, a measured quantity",
    proposed: "Direction only – a proposed metric, no measurement data",
    upper_bound: "upper bound",
    stops: "ends {{year}}",
  },
  report: {
    interplayIntro: "Before the vote, the workshop read how its solutions affect each other. The relations rest on the solutions' impact estimates, targets and pathways; each has a reason and an uncertainty.",
    independent: "{{count}} pairs of solutions were judged not to affect each other materially.",
    timelineIntro: "The workshop voted on which solutions to keep, and the choice was locked. Then it imagined how the locked solutions could be implemented over ten years in each scenario, judged realistically in that world. A vision, not a forecast: every fate rests on the solutions' own threats and the relations between them.",
    locked: "Carried into future testing: {{names}}",
    override: "The choice differed from the vote. The reason: {{reason}}",
    fates: "What happens to the solutions",
    moments: "What society could notice",
  },
  ...futureOutcomeEn,
};

export const futureReimaginedSv = {
  title: "Framtidstestning",
  intro: "Varje lösning har bedömts för sig. Här ser vi vad de gör med varandra, väljer vilka som behålls och föreställer oss hur den valda helheten kunde förverkligas. Det är en vision, inte en prognos: osäkerheterna sägs öppet.",
  loading: "Lösningarna laddas…",
  loadError: "Laddningen misslyckades: {{message}}",
  tooFew: "Växelverkan kan granskas när Lösningar v2 har minst två lösningar. Nu finns det {{count}}.",
  unprojected: "Dessa lösningar har ännu ingen konsekvensbedömning och saknas därför i jämförelsen av mätare: {{names}}",
  direction: { better: "Förbättrar", worse: "Försämrar", unclear: "Riktning utan fastställt bättre eller sämre" },
  scenarioEditor: {
    title: "Prognosens scenarier",
    intro: "Scenarierna är världarna där de låsta lösningarna föreställs och bedöms realistiskt. Välj varifrån de kommer.",
    source: "Källa",
    sourceHint: {
      expert: "De fyra scenarier som experter skrivit, efter optimismnivå. Samma i alla workshoppar.",
      workshop: "Workshopens egna scenarier från en tidigare modul (t.ex. Ennakointi). Läses som de är, redigeras inte här.",
      own: "Texter som skrivits för den här workshopen.",
    },
    inUse: "I bruk",
    use: "Använd den här källan",
    placeholder: "Expertscenarierna är ännu platshållare tills experternas texter läggs till.",
    workshopEmpty: "Workshopen har inga scenarier än. De kommer från en tidigare modul.",
    copyExpert: "Kopiera expertscenarierna för redigering",
    add: "Lägg till ett scenario",
    remove: "Ta bort scenariot",
    titleLabel: "Rubrik",
    levelLabel: "Optimismnivå",
    levelNone: "Ingen nivå",
    textLabel: "Text",
    chars: "{{n}} / {{max}} tecken",
    save: "Spara de egna scenarierna",
    saved: "Sparat.",
    reset: "Ta bort de egna texterna",
    tooFew: "Minst {{min}} scenarier med rubrik och text behövs.",
    loadError: "Det gick inte att läsa in scenarierna: {{message}}",
  },
  matrix: {
    title: "Vad varje lösning påverkar",
    hint: "Raderna är lösningar, kolumnerna hävstångspunkternas mätare. Färgen kommer från mätarens egen riktning, inte från AI:ns åsikt om lösningen.",
    sideEffect: "Bieffekt (inte eftersträvad)",
    untouched: "ingen lösning påverkar den",
  },
  candidates: {
    title: "Möjlig växelverkan",
    hint: "Beräknat utifrån lösningarnas konsekvensbedömningar, mål och genomförandevägar: vad två lösningar har gemensamt. Det säger ännu inte om de stöder eller konkurrerar med varandra.",
    none: "Lösningarna har inga gemensamma mätare, mål eller aktörer.",
    all: "Visa alla {{count}} par",
    fewer: "Visa färre",
  },
  signal: {
    similarity: "Påverkar samma mätare {{percent}} %",
    opposed: "Motsatta: {{name}}",
    same_harm: "Båda försämrar: {{name}}",
    unclear_opposed: "Motsatta riktningar: {{name}}",
    same_good: "Båda förbättrar: {{name}}",
    unclear_same: "Samma riktning: {{name}}",
    shared_target: "Samma mål: {{name}}",
    shared_actor: "Samma aktör: {{name}} ({{horizon}})",
  },
  interplay: {
    title: "Växelverkan",
    hintUnread: "Nedan syns vad lösningarna har gemensamt enligt siffrorna. AI:n läser paren och säger om lösningarna överlappar, konkurrerar, står i konflikt, möjliggör eller förstärker varandra – eller inte påverkar varandra alls.",
    hintRead: "Linjerna är AI:ns tolkning av hur lösningarna påverkar varandra. Välj en lösning för att bara se dess relationer. Varje relation anger sin grund och osäkerhet.",
    waiting: "AI:n har ännu inte läst paren. Facilitatorn startar läsningen, och resultatet visas här när det är klart.",
    readAt: "Läst {{time}}",
    stale: "Lösningar har ändrats eller lagts till efter denna läsning ({{names}}). Facilitatorn kan läsa växelverkan igen före omröstningen.",
  },
  graph: { label: "Relationer mellan lösningarna" },
  relation: { conflict: "Konflikt", overlap: "Överlappning", competing: "Konkurrens", enables: "Möjliggör", reinforces: "Förstärker", independent: "Ingen påverkan" },
  strength: { low: "Liten betydelse", medium: "Måttlig betydelse", high: "Stor betydelse" },
  basis: { research: "forskning", analogue: "jämförbart fall", reasoned: "resonemang" },
  cluster: { overlap: "Överlappande grupp:", competing: "Konkurrerande grupp:", dependency: "Vilar på en:" },
  relationCard: {
    why: "Varför: motiveringen, osäkerheten och grunden",
    uncertainty: "Osäkerhet: {{text}}",
    via: "Går via: {{list}}",
    basis: "Grund: {{basis}}",
    noneDrawn: "AI:n fann ingen överlappning, konkurrens, konflikt eller beroende mellan paren.",
    showIndependent: "Visa par som inte påverkar varandra ({{count}})",
    hideIndependent: "Dölj par som inte påverkar varandra ({{count}})",
    unanswered: "AI:n gav ingen bedömning för dessa par: {{pairs}}",
    ragUnavailable: "Forskningsmaterialet kunde inte nås, så relationerna vilar på resonemang.",
  },
  vote: {
    title: "Omröstning: vilka lösningar behålls",
    hint: "Välj de lösningar som tas med till framtidstestningen. Varje kort visar vad läsningen av växelverkan kopplar lösningen till. Tryck igen för att ta tillbaka en röst.",
    left: "Röster kvar {{left}} / {{budget}}",
    noIdentity: "Den här enheten kan inte rösta. Logga in med workshopens PIN-kod.",
    vote: "Rösta: {{name}}",
    unvote: "Ta tillbaka rösten: {{name}}",
    warning: { conflict: "I konflikt med: {{name}}", overlap: "Överlappar: {{name}}", competing: "Konkurrerar med: {{name}}", needs: "Behöver först: {{name}}", enables: "Möjliggör: {{name}}" },
    error: {
      CLOSED: "Omröstningen är stängd.",
      BUDGET: "Du har inga röster kvar. Ta tillbaka en röst för att rösta på en annan.",
      NO_SOLUTION: "Lösningen finns inte längre.",
      NO_VOTER: "Enheten kändes inte igen som röstare.",
      generic: "Rösten kunde inte sparas: {{message}}",
    },
  },
  locked: {
    title: "Tas med till framtidstestningen",
    votes: "(röster: {{count}})",
    override: "Valet avviker från omröstningen. Facilitatorns motivering: {{reason}}",
    stale: "Dessa lösningar har redigerats efter låsningen: {{names}}. Den föreställda tidslinjen bygger på den låsta versionen.",
    deleted: "(raderad lösning)",
  },
  admin: {
    title: "Framtidstestning: omröstning och låsning",
    loadError: "Laddningen misslyckades: {{message}}",
    interplayRead: "Växelverkan har lästs.",
    interplayStale: "Växelverkan har lästs, men lösningar har ändrats sedan dess. Läs den igen på modulens sida före omröstningen.",
    interplayMissing: "Växelverkan har inte lästs ännu. Det lönar sig att läsa den på modulens sida före omröstningen.",
    votes: "Omröstningen är {{open}}, {{budget}} röster per enhet. Öppna och stäng omröstningen med brytaren ovan.",
    open: "öppen",
    closed: "stängd",
    tension: {
      overlap: "Överlappning i urvalet: {{a}} och {{b}}",
      conflict: "Konflikt i urvalet: {{a}} och {{b}}. Du kan ändå låsa; tidslinjen visar vad det kostar.",
    },
    overrideHint: "Urvalet lämnar bort en lösning som fick fler röster än en vald. Dokumentera varför: rummet ser det och rapporten sparar det.",
    overridePlaceholder: "Varför avviker urvalet från omröstningen?",
    lock: "Lås {{count}} lösningar",
    relock: "Lås igen ({{count}})",
    lockFailed: "Låsningen misslyckades: {{message}}",
    openModule: "Öppna modulen",
    currentLock: "Nuvarande låsning ({{time}}): {{names}}",
  },
  course: {
    causes: "Motivering",
    title: "Föreställt förlopp: vad som kunde hända lösningarna",
    running: "Tidslinjen föreställs…",
    failed: "Det gick inte att föreställa tidslinjen: {{message}}",
    yearLikelihood: "{{year}} · {{likelihood}}",
    triggers: "Vad det beror på:",
    setAside: "Hot som inte bedöms förverkligas:",
    threat: "{{what}} ({{when}}, {{likelihood}})",
    relation: "{{relation}}: {{a}} – {{b}}",
    wouldChange: "Vad som skulle ändra detta: {{text}}",
    missing: "Inget grundat utfall kom för dessa lösningar: {{names}}",
    until: "till {{year}}",
    after: "först när {{name}} är igång",
  },
  fate: { realised: "Förverkligas", delayed: "Försenas", diminished: "Försvagas", failed: "Misslyckas" },
  likelihood: { low: "osannolik", medium: "möjlig", high: "sannolik" },
  timeline: {
    label: "Det föreställda genomförandet av de låsta lösningarna år för år",
    today: "I dag",
    haze: "Prognosens osäkerhet växer ju längre in i framtiden den sträcker sig",
    noNarrative: "Berättelsen kunde inte skrivas; förloppet och utfallen finns ändå nedan. {{message}}",
  },
  narrativeKind: { milestone: "Milstolpe", side_effect: "Bieffekt", setback: "Bakslag", turning_point: "Vändpunkt", interaction: "Möte" },
  wall: { vision: "En vision, inte en prognos: varje ögonblick vilar på lösningarnas egna hot och relationerna mellan dem, och dess osäkerhet anges." },
  robustness: {
    title: "Robusthet: hur lösningarna klarar sig i olika förlopp",
    verdict: "Sammanfattning",
    holds: "Håller i alla förlopp",
    failsAll: "Misslyckas i alla förlopp",
    fails: "Misslyckas i något förlopp",
    varies: "Varierar med förloppet",
    unknown: "Ingen information",
  },
  outlook: {
    title: "Vad som kunde hända med mätarna",
    hint: "Lösningar v2:s bedömningar av hävstångspunkternas mätare, placerade i detta förlopps år: en försenad lösning börjar senare, en som misslyckas slutar där den misslyckas. En försvagad lösnings kurva är en övre gräns, inte en bedömning av en mindre effekt. Kurvor för samma mätare läggs inte ihop.",
    measured: "Relativ förändring från i dag, en mätt storhet",
    proposed: "Endast riktning – en föreslagen mätare, inga mätdata",
    upper_bound: "övre gräns",
    stops: "slutar {{year}}",
  },
  report: {
    interplayIntro: "Före omröstningen lästes hur workshopens lösningar påverkar varandra. Relationerna bygger på lösningarnas konsekvensbedömningar, mål och genomförandevägar; var och en har en motivering och en osäkerhet.",
    independent: "{{count}} par av lösningar bedömdes inte påverka varandra nämnvärt.",
    timelineIntro: "Workshopen röstade om vilka lösningar som behålls, och valet låstes. Sedan föreställde den sig hur de låsta lösningarna kunde genomföras under tio år i varje scenario, bedömda realistiskt i den världen. En vision, inte en prognos: varje utfall vilar på lösningarnas egna hot och relationerna mellan dem.",
    locked: "Togs med till framtidstestningen: {{names}}",
    override: "Valet avvek från omröstningen. Motiveringen: {{reason}}",
    fates: "Vad som händer lösningarna",
    moments: "Vad samhället kunde märka",
  },
  ...futureOutcomeSv,
};
